import React from 'react';
import PageHero from '../components/PageHero';
import GetInvolved from '../components/GetInvolved';
import './About.css';

const TIMELINE = [
  { year: '2010', icon: '🌱', title: 'Founded', text: 'Shine People Trust registered in Chennai by a group of social workers committed to rural upliftment.' },
  { year: '2012', icon: '🎓', title: 'First School Program', text: 'Launched the Education for All initiative across 8 villages in Villupuram, reaching 300 children.' },
  { year: '2015', icon: '💊', title: 'Healthcare Wing', text: 'Established mobile medical camps; conducted 1,200 free health check-ups in year one.' },
  { year: '2017', icon: '👩', title: 'Women Empowerment', text: 'Formed 45 self-help groups and partnered with NABARD for micro-finance in 3 districts.' },
  { year: '2019', icon: '🏆', title: 'National Recognition', text: 'Received the National Award for Excellence in Rural Development from the Ministry of Panchayati Raj.' },
  { year: '2022', icon: '🌾', title: 'Sustainability Program', text: 'Launched rainwater harvesting and organic farming across 24 villages, benefiting 1,800+ farmers.' },
  { year: '2024', icon: '🚀', title: 'Digital Expansion', text: 'Introduced digital literacy labs in 20 rural schools; 2,000+ students trained in basic computing.' },
];

const TEAM = [
  { initial: 'R', name: 'Dr. Rajesh Kumar', role: 'Founder & Chairman', bio: 'Social activist with 25 years of grassroots development experience across South India.' },
  { initial: 'S', name: 'Sudha Annamalai', role: 'Executive Director', bio: 'Former UNICEF officer with expertise in child welfare and community health programs.' },
  { initial: 'V', name: 'Venkatesan M.', role: 'Program Director', bio: 'Leads field operations across 5 districts, overseeing 6 active programs and 80+ staff.' },
  { initial: 'P', name: 'Priya Chandran', role: 'Finance & Compliance', bio: 'CPA with NGO sector expertise ensuring full transparency and donor accountability.' },
];

const AWARDS = [
  { icon: '🏆', title: 'National NGO Excellence Award', by: 'Ministry of Panchayati Raj', year: '2019' },
  { icon: '🌟', title: 'Best Rural Development Initiative', by: 'NITI Aayog India', year: '2021' },
  { icon: '💡', title: 'Innovation in Education Award', by: 'UNESCO India', year: '2022' },
  { icon: '🤝', title: 'CSR Partner of the Year', by: 'CII Southern Region', year: '2023' },
  { icon: '🌿', title: 'Green Champion Award', by: 'Tamil Nadu Govt.', year: '2023' },
  { icon: '❤️', title: 'Outstanding Healthcare Outreach', by: 'IMA Tamil Nadu', year: '2024' },
];

export default function About() {
  return (
    <main id="main-content">
      <PageHero
        label="Our Story"
        title="15 Years of Changing Lives"
        desc="From a small group of dedicated volunteers to a nationally recognised NGO — learn the story, values, and people behind Shine People Trust."
        breadcrumb="About Us"
        bgImage="https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?auto=format&fit=crop&w=1600&h=600&q=80"
      />

      {/* Story */}
      <section className="about-story section" aria-labelledby="story-heading">
        <div className="container">
          <div className="story-inner">
            <div className="story-visual">
              <div className="story-img-main">
                <img
                  src="https://images.unsplash.com/photo-1601689892697-b64daa00ff6d?auto=format&fit=crop&w=700&h=875&q=80"
                  alt="Community members working together for rural development"
                  className="story-img"
                  loading="lazy"
                />
                <div className="story-img-overlay" aria-hidden="true" />
              </div>
              <div className="story-founded-badge" aria-hidden="true">
                <div className="story-founded-year">2010</div>
                <div className="story-founded-label">Founded</div>
              </div>
              <div className="story-award-badge" aria-hidden="true">
                <div className="story-award-icon">🏆</div>
                <div className="story-award-text">
                  <strong>6 National Awards</strong>
                  <span>& Counting</span>
                </div>
              </div>
            </div>

            <div className="story-content">
              <div className="section-header" style={{ textAlign: 'left', margin: 0 }}>
                <span className="section-label">How It Began</span>
                <h2 className="section-title" id="story-heading">
                  Born from a Belief That Every Person Deserves to Shine
                </h2>
                <p className="section-subtitle">
                  In 2010, Dr. Rajesh Kumar witnessed children in a remote Tamil Nadu village walking miles to school, only to sit in crumbling classrooms. That moment sparked what would become Shine People Trust.
                </p>
                <p className="section-subtitle" style={{ marginTop: '1rem' }}>
                  Starting with just 12 volunteers and a modest grant, the Trust began renovating school buildings and providing scholarships. Word spread. More volunteers came. Programmes grew — healthcare, women's empowerment, sustainable farming. Today, with a full-time team of 80+, we operate across 5 districts, serve 120+ villages, and have touched more than 50,000 lives.
                </p>
              </div>

              <blockquote className="story-quote">
                <p className="story-quote-text">
                  "We do not give charity — we build capacity. Every family we work with becomes a partner in their own development."
                </p>
                <cite className="story-quote-author">— Dr. Rajesh Kumar, Founder</cite>
              </blockquote>
            </div>
          </div>
        </div>
      </section>

      {/* Mission / Vision / Values */}
      <section className="about-mvv section" aria-labelledby="mvv-heading">
        <div className="container">
          <div className="section-header">
            <span className="section-label">What Drives Us</span>
            <h2 className="section-title" id="mvv-heading">Mission, Vision & Values</h2>
          </div>
          <div className="mvv-grid" role="list">
            <div className="mvv-card mvv-card-mission" role="listitem">
              <div className="mvv-icon mvv-icon-teal" aria-hidden="true">🎯</div>
              <h3 className="mvv-title">Our Mission</h3>
              <p className="mvv-text">
                To empower underserved communities through sustainable, community-led development in education, healthcare, livelihood, and environment — ensuring every individual can realise their full potential.
              </p>
            </div>
            <div className="mvv-card mvv-card-vision" role="listitem">
              <div className="mvv-icon mvv-icon-amber" aria-hidden="true">🌅</div>
              <h3 className="mvv-title">Our Vision</h3>
              <p className="mvv-text">
                A society where no person is held back by poverty, lack of education, or ill health — where every community has the tools, voice, and opportunity to shape its own future.
              </p>
            </div>
            <div className="mvv-card mvv-card-values" role="listitem">
              <div className="mvv-icon mvv-icon-blue" aria-hidden="true">💎</div>
              <h3 className="mvv-title">Our Values</h3>
              <ul className="mvv-values-list" aria-label="Core values">
                {['Transparency & Accountability', 'Community Ownership', 'Inclusivity & Dignity', 'Sustainability', 'Compassionate Action'].map(v => (
                  <li className="mvv-value-item" key={v}>
                    <span className="mvv-value-dot" aria-hidden="true" />
                    {v}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="about-timeline section" aria-labelledby="timeline-heading">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Our Journey</span>
            <h2 className="section-title" id="timeline-heading">Milestones That Shaped Us</h2>
            <p className="section-subtitle">Fifteen years of growth, learning, and impact — one milestone at a time.</p>
          </div>

          <div className="timeline" role="list">
            {TIMELINE.map((item, i) => (
              <div className="timeline-item" key={item.year} role="listitem">
                {i % 2 === 0 ? (
                  <>
                    <div className="timeline-content">
                      <div className="timeline-card">
                        <div className="timeline-card-title">{item.title}</div>
                        <div className="timeline-card-text">{item.text}</div>
                      </div>
                    </div>
                    <div className="timeline-node" aria-label={item.year}>
                      <div className="timeline-dot" aria-hidden="true">{item.icon}</div>
                      <div className="timeline-year">{item.year}</div>
                    </div>
                    <div className="timeline-spacer" />
                  </>
                ) : (
                  <>
                    <div className="timeline-spacer" />
                    <div className="timeline-node" aria-label={item.year}>
                      <div className="timeline-dot" aria-hidden="true">{item.icon}</div>
                      <div className="timeline-year">{item.year}</div>
                    </div>
                    <div className="timeline-content">
                      <div className="timeline-card">
                        <div className="timeline-card-title">{item.title}</div>
                        <div className="timeline-card-text">{item.text}</div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="about-team section" aria-labelledby="team-heading">
        <div className="container">
          <div className="section-header">
            <span className="section-label">The People Behind the Work</span>
            <h2 className="section-title" id="team-heading">Meet Our Leadership Team</h2>
            <p className="section-subtitle">Passionate professionals committed to our mission, bringing decades of combined experience in social development.</p>
          </div>
          <div className="team-grid" role="list">
            {TEAM.map(m => (
              <div className="team-card" key={m.name} role="listitem">
                <div className="team-avatar-wrap">
                  <div className="team-avatar" aria-hidden="true">{m.initial}</div>
                </div>
                <div className="team-body">
                  <div className="team-name">{m.name}</div>
                  <div className="team-role">{m.role}</div>
                  <p className="team-bio">{m.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Awards */}
      <section className="about-awards section" aria-labelledby="awards-heading">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Recognition</span>
            <h2 className="section-title" id="awards-heading">Awards & Recognitions</h2>
            <p className="section-subtitle">Our work has been recognised by government bodies, international organisations, and industry peers.</p>
          </div>
          <div className="awards-grid" role="list">
            {AWARDS.map(a => (
              <div className="award-card" key={a.title} role="listitem">
                <div className="award-icon" aria-hidden="true">{a.icon}</div>
                <div>
                  <div className="award-title">{a.title}</div>
                  <div className="award-by">{a.by}</div>
                  <div className="award-year">{a.year}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <GetInvolved />
    </main>
  );
}
